import type { JSX } from 'react';
import type { ShareholderInput } from '@/types';
import { validateTaxCode } from '@/utils/taxCodeValidation';
import { CharityInput } from '../CharityInput';
import { PensionInput } from '../PensionInput';
import { Input, CollapsibleSection } from '../ui';

interface Props {
  role: 'Director' | 'Partner';
  data: ShareholderInput;
  setData: (value: ShareholderInput) => void;
  showAdditionalIncome?: boolean;
}

export function ShareholderFormSection({
  role,
  data,
  setData,
  showAdditionalIncome = false,
}: Props): JSX.Element {
  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-lg">{role} Details</h3>

      {/* Standard Fields */}
      <Input
        label="Salary"
        helperText={
          role === 'Director'
            ? 'Optimal: £4,996 (solo) or £12,570 (with EA)'
            : 'Typically £12,570 (full personal allowance)'
        }
        type="number"
        prefix="£"
        value={data.gross_salary}
        onChange={(e) => setData({ ...data, gross_salary: Number(e.target.value) || 0 })}
        min={0}
        step={10}
        fullWidth
        selectAllOnFocus
      />

      <Input
        label="Tax Code"
        helperText="E.g., 1257L (standard), BR (basic rate), 0T (no allowance)"
        type="text"
        value={data.tax_code}
        onChange={(e) => setData({ ...data, tax_code: e.target.value })}
        error={data.tax_code ? validateTaxCode(data.tax_code).error : undefined}
        fullWidth
      />

      {/* Contractor-specific Fields */}
      <Input
        label="Shareholding %"
        helperText="Must total 100% with other shareholders"
        type="number"
        suffix="%"
        value={data.shareholding_percentage}
        onChange={(e) =>
          setData({ ...data, shareholding_percentage: Number(e.target.value) || 0 })
        }
        min={0}
        max={100}
        step={1}
        fullWidth
        selectAllOnFocus
      />

      {/* Pension Section - Reused from PAYE */}
      <PensionInput
        percentageValue={data.net_pension_contributions_percentage ?? 0}
        amountValue={data.net_pension_contributions_amount ?? 0}
        activeMode={data.net_pension_contributions_type || 'amount'}
        onChange={(value, type) => {
          if (type === 'percentage') {
            setData({
              ...data,
              net_pension_contributions_percentage: value,
              net_pension_contributions_type: 'percentage',
            });
          } else {
            setData({
              ...data,
              net_pension_contributions_amount: value,
              net_pension_contributions_type: 'amount',
            });
          }
        }}
        label="Personal Pension Contributions"
        pensionType={data.pension_type || 'relief_at_source'}
        onPensionTypeChange={(type) => setData({ ...data, pension_type: type })}
      />

      {/* Charity Section - Reused from PAYE */}
      <CharityInput
        value={
          data.charity_type === 'gift_aid'
            ? data.gift_aid_donations ?? 0
            : data.payroll_giving_donations ?? 0
        }
        onChange={(value) => {
          if (data.charity_type === 'gift_aid') {
            setData({ ...data, gift_aid_donations: value });
          } else {
            setData({ ...data, payroll_giving_donations: value });
          }
        }}
        charityType={data.charity_type || 'gift_aid'}
        onCharityTypeChange={(type) => setData({ ...data, charity_type: type })}
      />

      {/* Additional Income - Only for Director */}
      {showAdditionalIncome && (
        <CollapsibleSection title="Additional Income (Optional)">
          <div className="space-y-4">
            <Input
              label="Self-Employment Income"
              helperText="Income from sole trader activities"
              type="number"
              prefix="£"
              value={data.self_employment_income ?? 0}
              onChange={(e) =>
                setData({ ...data, self_employment_income: Number(e.target.value) || 0 })
              }
              min={0}
              step={100}
              fullWidth
              selectAllOnFocus
            />

            <Input
              label="Other Income"
              helperText="Rental, investment income, etc."
              type="number"
              prefix="£"
              value={data.other_income ?? 0}
              onChange={(e) => setData({ ...data, other_income: Number(e.target.value) || 0 })}
              min={0}
              step={100}
              fullWidth
              selectAllOnFocus
            />
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
